// eslint-disable-next-line import/no-named-as-default
import { Icon } from "@mdi/react";
import { useEffect, useMemo, useState } from "react";

import { mdiChevronDown } from "@mdi/js";
import {
  Alert,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Switch,
  TreeSelect
} from "antd";
import { EVA_RESEARCH_NAME } from "constants/app.constants";
import moment from "moment";
import styled from "styled-components";
import { v4 as uuidv4 } from "uuid";

import usePlayTo3DDataPrivateSources from "hooks/usePlayTo3DDataPrivateSources";
import useUserModules from "hooks/useUserModules";

import { getValidWells } from "api/data";
import { getGroupedPlayZonesByPeriod } from "api/map";

import { FilterScope, SavedFilterModel } from "models";

import getGeoMapLayers from "components/geo-map/hooks/getGeoMapLayers";
import { MapLayer } from "components/geo-map/models/mapLayer";
import SegmentedToggle from "components/shared/SegmentedToggle";

import { useLengthConversion, useUser } from "../../../hooks";
import { PrimarySourceSelect } from "../PrimarySourceSelect";
import { usePostGeoCalculationJob } from "../hooks/usePostGeoCalculationJob";
import { GeoCalculationsJob, ScheduleFrequency } from "../models/GeoCalculations";

export const convertToTreeData = (data) => {
  return data.map((period) => ({
    title: period.period,
    value: `period_${period.period}`,
    key: `period_${period.period}`,
    type: `period`,
    checkable: false,
    children: period.plays.map((play) => ({
      title: play.play,
      value: `play_${play.play}`,
      key: `play_${play.play}`,
      type: `play`,
      children: play.zones.map((zone) => ({
        title: zone,
        value: zone,
        key: zone,
        type: `zone`
      }))
    }))
  }));
};

// Constants for better maintainability
const ZONE_SEPARATOR = "map_";

/**
 * Processes zone values based on whether a model source is selected.
 * For model sources, extracts zone names from the format "id_map_zoneName".
 * For default sources, returns zones as-is.
 */
export const processZonesForModelSource = (
  zones: string[] | undefined,
  modelSource: string
): string[] => {
  if (!zones?.length) {
    return [];
  }

  // If no model source selected, return zones unchanged
  if (!modelSource) {
    return zones;
  }

  // Extract zone names from model source format using constant
  return zones
    .map((zone) => {
      const separatorIndex = zone.lastIndexOf(ZONE_SEPARATOR);
      if (separatorIndex !== -1) {
        const zoneName = zone.substring(separatorIndex + ZONE_SEPARATOR.length);
        return zoneName; // Return zoneName (could be empty)
      }
      return zone; // Return original if separator not found
    })
    .filter((zone) => zone && zone.trim() !== ""); // Remove empty or whitespace-only strings
};

const { TextArea } = Input;
export const CreateGeoCalculationJobModal = ({
  requestShowModal,
  jobs,
  onClose,
  savedFilters
}: CreateAccessTokenModalProps) => {
  const [isModalVisible, setIsModalVisible] = useState(requestShowModal);
  const [inputMode, setInputMode] = useState<"wellList" | "savedFilter">("wellList");
  const { email } = useUser();
  const [errors, setErrors] = useState<string>("");

  const [periodKeys, setPeriodKeys] = useState([]);
  const [selectedIpdbZones, setSelectedIpdbZones] = useState<string[]>([]);
  const [treeData, setTreeData] = useState([]);
  const [form] = Form.useForm();
  const [isScheduled, setIsScheduled] = useState(false);

  const { convert, convertBack, getDisplayUnit } = useLengthConversion();

  const { mutateAsync, isError, error } = usePostGeoCalculationJob();

  const { data: mapLayers } = getGeoMapLayers();

  const [modelSource, setModelSource] = useState("");

  const {
    data: modelSources,
    refetch: refetchDataSources,
    isLoading
  } = usePlayTo3DDataPrivateSources();

  useEffect(() => {
    refetchDataSources();
  }, []);

  // EVA-150: "EVA Research" is the McDaniel geo model, gated per play by TWO tiers — Geology
  // unlocks the XDA/spacing/parent-child columns, and 3D Geo Model additionally unlocks the
  // sampled-3D columns. Either tier makes the option worth offering, so only an org with neither
  // has it hidden; requiring 3D would wrongly deny a Geology-only org the columns it does pay for.
  // The org's own imported model sources are always listed.
  const { hasGeology, has3dGeoModel } = useUserModules();
  const geoSourceOptions = useMemo(
    () => [
      ...(hasGeology || has3dGeoModel ? [{ label: EVA_RESEARCH_NAME, value: "" }] : []),
      ...(modelSources || []).map((source) => ({
        label: source.source,
        value: source.source
      }))
    ],
    [hasGeology, has3dGeoModel, modelSources]
  );

  // EVA-150 (blocker from review): hiding the option does not change what gets submitted.
  // EVA_RESEARCH_NAME's value is the empty string and `modelSource` starts as "", so an org with
  // no Geology/3D subscription but with private models would open this modal, see the Select
  // render blank, never touch it, and still post modelSource: "" — the gated McDaniel source. The
  // backend fails closed so nothing leaks, but the user gets exactly the empty job this guard
  // exists to prevent. Keep the selection inside whatever the dropdown currently offers.
  useEffect(() => {
    if (!geoSourceOptions.some((option) => option.value === modelSource)) {
      const fallback = geoSourceOptions[0]?.value ?? "";
      setModelSource(fallback);
      form.setFieldsValue({ modelSource: fallback });
    }
  }, [geoSourceOptions, modelSource, form]);

  useEffect(() => {
    if (isLoading) return;

    if (modelSource === "") {
      getGroupedPlayZonesByPeriod().then((response) => {
        const treeData = convertToTreeData(response.data) ?? [];
        setPeriodKeys(response.data.map((p) => `period_${p.period}`));
        setTreeData(treeData);
      });
    } else if (modelSources?.length) {
      const selectedSource = modelSources.find((source) => source.source === modelSource);

      // EVA-150: never dereference this lookup blind. `modelSource` can legitimately name a source
      // that is not in the current list — the correction effect above only lands on the NEXT
      // render, so this effect runs once with the stale value, and a refetch can drop or rename
      // the selected source outright. Clear the zone tree and wait rather than throwing and
      // taking the whole modal down.
      if (!selectedSource) {
        setTreeData([]);
        setSelectedIpdbZones([]);
        return;
      }

      const treeData = selectedSource.plays.map((play) => {
        let selectedLayers = mapLayers.filter((l) => l.group === play);

        if (selectedLayers.length === 0) {
          // If no layers found for the play, use the model source as a fallback
          selectedLayers = mapLayers.filter((l) => l.group === modelSource);
        }

        return {
          title: play,
          value: `group_${play}`,
          key: `play_${play}`,
          type: `play`,
          checkable: false,
          children: selectedLayers.map((layer: MapLayer) => ({
            title: layer.name,
            value: `${layer._id}_map_${layer.name}`,
            key: `${layer._id}_map_${layer.name}`,
            type: `play`
          }))
        };
      });
      setTreeData(treeData);
    }
  }, [modelSource, modelSources, isLoading]);

  useEffect(() => {
    setIsModalVisible(requestShowModal);
    //updateProjects();
  }, [requestShowModal]);

  const handleOk = async () => {
    try {
      // EVA-150 (review): checked BEFORE validateFields - this is a precondition on the org,
      // not on anything the user typed, and an empty form would otherwise fail validation first
      // and hide it.
      // an empty option list means the private-source request failed and the
      // org holds neither tier — useCanCreateGeoJob deliberately allows the modal in that case so
      // a legitimate org isn't locked out by a network blip. But the correction effect's `?? ""`
      // fallback is EVA_RESEARCH_NAME's value, i.e. the gated source, so submitting here would
      // post exactly what the option-hiding was meant to prevent. Block it at the boundary.
      if (geoSourceOptions.length === 0) {
        setErrors(
          "No geo source is available right now. Reload the page, or contact support if this persists."
        );
        return;
      }

      const values = await form.validateFields();

      const formattedName = (name) => name.trim().toLowerCase();
      const name = formattedName(values.name);

      if (jobs.some((job) => formattedName(job.name) === name)) {
        setErrors("Job name already exists.");
        return;
      }

      if (
        values.isScheduled &&
        (!values.scheduleFrequency || !values.scheduleStartDate)
      ) {
        setErrors(
          "Schedule frequency and start date are required when scheduling is enabled."
        );
        return;
      }

      if (values.includeSampled3D === false && !values.includeXda) {
        setErrors(
          "At least one calculation is required to proceed with the Geo Calculation job."
        );
        return;
      }

      let validWells: string[] = [];
      let savedFilterId: string | undefined;
      let savedFilterJson: string | undefined;
      let savedFilterName: string | undefined;

      if (inputMode === "wellList") {
        if (!values.wellList) {
          setErrors("A well list is required.");
          return;
        }
        const wells = values.wellList.split("\n").map((item: string) => item.trim());
        validWells = await getValidWells(wells);
        if (validWells.length === 0) {
          setErrors("0 valid wells in the well list.");
          return;
        }
      } else {
        if (!values.savedFilterId) {
          setErrors("A saved filter is required.");
          return;
        }
        savedFilterId = values.savedFilterId;
        const savedFilter = savedFilters.find((f) => f.id === savedFilterId);
        savedFilterJson = savedFilter?.filterJSON;
        savedFilterName = savedFilter?.name;
      }

      const zones = processZonesForModelSource(values.zones, values.modelSource);

      const scheduleStartDateTimestamp =
        values.isScheduled && values.scheduleStartDate
          ? {
              $date: {
                $numberLong: values.scheduleStartDate
                  .startOf("day")
                  .add(1, "minute")
                  .valueOf()
                  .toString()
              }
            }
          : null;

      const postData: GeoCalculationsJob = {
        jobId: uuidv4(),
        name: values.name,
        leftWidth: parseFloat(values.leftWidth),
        rightWidth: parseFloat(values.rightWidth),
        upHeight: parseFloat(values.upHeight),
        downHeight: parseFloat(values.downHeight),
        savedFilterId: savedFilterId ?? "",
        savedFilterJson: savedFilterJson,
        savedFilterName: savedFilterName,
        wellList: validWells,
        email,
        zones,
        modelSource: values.modelSource,
        primarySource: values.primarySource,
        includeSampled3D: values.includeSampled3D ?? true,
        includeXda: values.includeXda ?? false,
        isScheduled: values.isScheduled ?? false,
        scheduleFrequency: values.isScheduled ? values.scheduleFrequency : null,
        scheduleStartDate: scheduleStartDateTimestamp,
        nextScheduledRun: scheduleStartDateTimestamp
      };

      await mutateAsync(postData);

      setErrors("");
      onClose && onClose();
    } catch (e) {
      setErrors("Error occurred while creating job");
      return;
    }
  };

  const requiredRule = { required: true, message: "This field is required" };

  return (
    <RootContainer>
      <Modal
        open={isModalVisible}
        onOk={handleOk}
        onCancel={onClose}
        title={
          <ModalTitleRow>
            <span>Add New Geo Calculation Job</span>
            <ToggleWrapper>
              <SegmentedToggle
                data-testid="use-saved-filter-toggle"
                value={inputMode}
                options={[
                  { label: "Well List", value: "wellList" },
                  { label: "Saved Filter", value: "savedFilter" }
                ]}
                onChange={(val) => setInputMode(val as "wellList" | "savedFilter")}
                block
                size="large"
                height={34}
              />
            </ToggleWrapper>
          </ModalTitleRow>
        }
        closable={false}
        maskClosable={false}
        width={"700px"}>
        <Form
          name="edit"
          form={form}
          initialValues={{
            remember: true,
            leftWidth: 300,
            rightWidth: 300,
            upHeight: 50,
            downHeight: 50
          }}
          onFinish={handleOk}
          requiredMark={false}>
          <Form.Item name="name" label={<Label>Name</Label>} rules={[requiredRule]}>
            <Input data-testid="job-name-input" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="leftWidth"
                label={<Label>Left Width ({getDisplayUnit()})</Label>}
                rules={[requiredRule]}
                getValueFromEvent={(value) => convertBack(value, -1)}
                getValueProps={(value) => ({
                  value: value != null ? convert(value, 0) : value
                })}>
                <InputNumber
                  data-testid="left-width-input"
                  precision={0}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="rightWidth"
                label={<Label>Right Width ({getDisplayUnit()})</Label>}
                rules={[requiredRule]}
                getValueFromEvent={(value) => convertBack(value, -1)}
                getValueProps={(value) => ({
                  value: value != null ? convert(value, 0) : value
                })}>
                <InputNumber
                  data-testid="right-width-input"
                  precision={0}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="upHeight"
                label={<Label>Up Height ({getDisplayUnit()})</Label>}
                rules={[requiredRule]}
                getValueFromEvent={(value) => convertBack(value, -1)}
                getValueProps={(value) => ({
                  value: value != null ? convert(value, 0) : value
                })}>
                <InputNumber
                  data-testid="up-height-input"
                  precision={0}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="downHeight"
                label={<Label>Down Height ({getDisplayUnit()})</Label>}
                rules={[requiredRule]}
                getValueFromEvent={(value) => convertBack(value, -1)}
                getValueProps={(value) => ({
                  value: value != null ? convert(value, 0) : value
                })}>
                <InputNumber
                  data-testid="down-height-input"
                  precision={0}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>
          {inputMode === "wellList" ? (
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item name="wellList" label={<Label>Well List</Label>}>
                  <TextArea
                    data-testid="well-list-input"
                    autoSize={{ minRows: 15, maxRows: 15 }}
                  />
                </Form.Item>
              </Col>
            </Row>
          ) : (
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item name="savedFilterId" label={<Label>Saved Filter</Label>}>
                  <Select
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label?.toString() ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    data-testid="saved-filter-select"
                    placeholder="Select a Saved Filter"
                    options={(() => {
                      // Helper function to create flat groups with scope + project name
                      const createFilterGroups = (
                        scopeName: string,
                        filters: SavedFilterModel[],
                        isSystemScope: boolean
                      ) => {
                        // Group by project name
                        const projectGroups = new Map<string, SavedFilterModel[]>();

                        filters.forEach((filter) => {
                          const projectKey = filter.projectName || "All Projects";
                          const existing = projectGroups.get(projectKey);
                          if (!existing) {
                            projectGroups.set(projectKey, [filter]);
                          } else {
                            existing.push(filter);
                          }
                        });

                        // Create flat groups with combined labels
                        return Array.from(projectGroups.entries())
                          .sort(([a], [b]) => {
                            // "All Projects" always comes first
                            if (a === "All Projects") return -1;
                            if (b === "All Projects") return 1;
                            return a.localeCompare(b);
                          })
                          .map(([projectName, projectFilters]) => {
                            let label: string;
                            if (isSystemScope) {
                              // System filters always show as "System Filters"
                              label = "System Filters";
                            } else if (projectName === "All Projects") {
                              // Filters without project name
                              label = scopeName;
                            } else {
                              // Filters with project name
                              label = `Project › ${projectName}`;
                            }

                            return {
                              label,
                              options: projectFilters.map((filter) => ({
                                value: filter.id,
                                label: filter.name
                              }))
                            };
                          });
                      };

                      // Group filters by FilterScope
                      const systemFiltersData = (savedFilters ?? []).filter(
                        (f) => f.filterScope === FilterScope.System
                      );
                      const orgFiltersData = (savedFilters ?? []).filter(
                        (f) => f.filterScope === FilterScope.Org
                      );
                      const teamFiltersData = (savedFilters ?? []).filter(
                        (f) => f.filterScope === FilterScope.Team
                      );
                      const personalFiltersData = (savedFilters ?? []).filter(
                        (f) => f.filterScope === FilterScope.Personal
                      );

                      const options = [];

                      // Maintain order: System -> Org -> Team -> Personal
                      if (systemFiltersData.length > 0) {
                        options.push(
                          ...createFilterGroups("System", systemFiltersData, true)
                        );
                      }

                      if (orgFiltersData.length > 0) {
                        options.push(
                          ...createFilterGroups("Organization", orgFiltersData, false)
                        );
                      }

                      if (teamFiltersData.length > 0) {
                        options.push(
                          ...createFilterGroups("Team", teamFiltersData, false)
                        );
                      }

                      if (personalFiltersData.length > 0) {
                        options.push(
                          ...createFilterGroups("Personal", personalFiltersData, false)
                        );
                      }

                      return options;
                    })()}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="primarySource" label={<Label>Survey Source</Label>}>
                <PrimarySourceSelect />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="modelSource" label={<Label>Geo Source</Label>}>
                <Select
                  data-testid="model-source-select"
                  value={modelSource}
                  options={geoSourceOptions}
                  onChange={(e) => {
                    setModelSource(e);
                    setSelectedIpdbZones([]);
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="zones" label={<Label>Zones</Label>}>
                <TreeSelect
                  showSearch
                  data-testid="geo-zone-select"
                  switcherIcon={
                    <SwitcherIcon>{<Icon path={mdiChevronDown} size={1} />}</SwitcherIcon>
                  }
                  value={selectedIpdbZones}
                  dropdownStyle={{ overflow: "auto" }}
                  placeholder="Please select"
                  allowClear
                  multiple
                  maxTagCount={10}
                  onChange={setSelectedIpdbZones}
                  treeDefaultExpandedKeys={periodKeys}
                  treeCheckable={true}
                  treeData={treeData}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="includeSampled3D"
                label={<Label>Sampled 3D</Label>}
                valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="includeXda"
                label={<Label>XDA</Label>}
                valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="isScheduled"
                label={<Label>Schedule Job</Label>}
                valuePropName="checked">
                <Switch checked={isScheduled} onChange={setIsScheduled} />
              </Form.Item>
            </Col>
          </Row>
          {isScheduled && (
            <>
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name="scheduleFrequency"
                    label={<Label>Frequency</Label>}
                    rules={[
                      { required: isScheduled, message: "This field is required" }
                    ]}>
                    <Select
                      placeholder="Select frequency"
                      options={[
                        { label: "Weekly", value: ScheduleFrequency.Weekly },
                        { label: "Monthly", value: ScheduleFrequency.Monthly }
                      ]}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name="scheduleStartDate"
                    label={<Label>Start Date</Label>}
                    rules={[
                      { required: isScheduled, message: "This field is required" }
                    ]}>
                    <DatePicker
                      style={{ width: "100%" }}
                      format="YYYY-MM-DD"
                      disabledDate={(current) =>
                        current && current < moment().startOf("day")
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}
        </Form>

        {(isError || errors) && (
          <ErrorMessage
            type="error"
            message={`${
              error?.response?.data ?? errors ?? "Error occurred while creating job"
            }`}
          />
        )}
      </Modal>
    </RootContainer>
  );
};

const RootContainer = styled.div``;

const ModalTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-right: 32px; /* avoid overlap with antd close button area */
`;

const ToggleWrapper = styled.div`
  width: 220px;
  flex-shrink: 0;
`;

const Label = styled.span`
  display: block;
  width: 110px;
`;

const SwitcherIcon = styled.span`
  transform: translateY(2px);
  color: rgba(var(--color-text-rgb), 0.3);
`;
const ErrorMessage = styled(Alert)`
  margin-bottom: 1em;
`;

interface CreateAccessTokenModalProps {
  requestShowModal: boolean;
  jobs: GeoCalculationsJob[];
  onClose: () => void;
  savedFilters: SavedFilterModel[];
}
